/**
 * Cart drawer — Task 35.1
 * Uses Radix Dialog (same as Modal) for reliable mobile overlay + portal.
 */
import { useEffect } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { useTranslation } from 'react-i18next'
import { X, Minus, Plus, Trash2, AlertCircle, Trash, ShoppingBag } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useFeatures } from '@/hooks/useFeatures'
import { useAuthStore } from '@/stores/authStore'
import { useCartStore } from '@/stores/cartStore'
import { useCart, useUpdateCartItem, useRemoveCartItem } from '@/hooks/useCart'
import { useUIStore } from '@/stores/uiStore'
import { useStoreLogo } from '@/hooks/useStoreLogo'
import { formatCurrency } from '@/lib/utils'
import { Button } from '@/components/ui/Button'
import { CouponInput } from './CouponInput'
import { ProductThumbnail } from './ProductThumbnail'

export function CartDrawer() {
  const { t } = useTranslation('cart')
  const { isOpen, closeCart } = useCartStore()
  const { currency } = useUIStore()
  const { features } = useFeatures()
  const { isAuthenticated } = useAuthStore()
  const canCheckout = isAuthenticated || features.enable_guest_checkout
  const storeLogoSrc = useStoreLogo()
  const { data: cart, isPending, isError, refetch } = useCart()
  const updateItem = useUpdateCartItem()
  const removeItem = useRemoveCartItem()

  const itemCount = cart?.items.length ?? 0
  const showEmpty = !isPending && (!cart || itemCount === 0)

  useEffect(() => {
    if (!isOpen) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [isOpen])

  const handleClearCart = async () => {
    if (!cart?.items.length) return
    if (!globalThis.confirm('Are you sure you want to clear all items from your cart?')) return

    for (const item of cart.items) {
      await removeItem.mutateAsync(item.id)
    }
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && closeCart()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />

        <Dialog.Content
          aria-describedby={undefined}
          className="fixed bottom-0 right-0 top-0 z-[201] grid w-[min(85vw,28rem)] grid-rows-[auto_1fr_auto] bg-white shadow-2xl outline-none focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right"
          style={{ height: '100dvh', maxHeight: '100dvh' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-4 sm:px-6">
            <Dialog.Title className="flex min-w-0 items-center gap-2 font-heading text-lg font-bold text-gray-900 sm:text-xl">
              <span className="truncate">{t('your_cart')}</span>
              {itemCount > 0 && (
                <span className="shrink-0 rounded-full bg-primary-50 px-2.5 py-1 text-sm font-medium text-primary-600">
                  {t('item_count', { count: itemCount })}
                </span>
              )}
            </Dialog.Title>
            <Dialog.Close
              className="shrink-0 rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
              aria-label="Close cart"
            >
              <X className="h-5 w-5" />
            </Dialog.Close>
          </div>

          {/* Body */}
          <div className="min-h-0 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6">
            {isPending ? (
              <div className="flex min-h-[12rem] items-center justify-center text-gray-500">
                <span className="animate-pulse font-medium">Loading your items…</span>
              </div>
            ) : isError ? (
              <div className="flex min-h-[12rem] flex-col items-center justify-center gap-3 text-center">
                <p className="text-sm text-gray-600">Could not load your cart.</p>
                <Button variant="outline" size="sm" onClick={() => refetch()}>
                  Try again
                </Button>
              </div>
            ) : showEmpty ? (
              <div className="flex min-h-[12rem] flex-col items-center justify-center gap-4 py-8 text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-50 text-primary-600">
                  <ShoppingBag className="h-8 w-8" />
                </div>
                <div>
                  <p className="text-lg font-bold font-heading text-gray-800">{t('empty_cart')}</p>
                  <p className="mt-1 text-sm text-gray-500">Add items to get started!</p>
                </div>
                <Button variant="outline" onClick={closeCart} asChild className="rounded-xl px-6">
                  <Link to="/products">{t('empty_cart_cta')}</Link>
                </Button>
              </div>
            ) : (
              <ul className="space-y-4">
                {cart!.items.map((item) => (
                  <li
                    key={item.id}
                    className="flex gap-3 rounded-2xl border border-gray-100 bg-white p-3 shadow-sm sm:gap-4"
                  >
                    <ProductThumbnail
                      product={item.product}
                      fallbackSrc={storeLogoSrc}
                      alt={item.product?.name}
                      className="h-16 w-16 shrink-0 rounded-xl border border-gray-100 sm:h-20 sm:w-20"
                    />

                    <div className="flex min-w-0 flex-1 flex-col justify-between">
                      <div>
                        <div className="flex items-start justify-between gap-2">
                          <Link
                            to={`/products/${item.product?.slug ?? ''}`}
                            className="min-w-0 flex-1 font-heading text-sm font-semibold text-gray-800 line-clamp-2 hover:text-primary-600"
                            onClick={closeCart}
                          >
                            {item.product?.name ?? 'Unknown product'}
                          </Link>
                          <button
                            type="button"
                            onClick={() => removeItem.mutate(item.id)}
                            className="shrink-0 p-1 text-gray-400 hover:text-red-500"
                            aria-label="Remove item"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>

                        {item.variant && (
                          <p className="mt-1 text-xs text-gray-500">
                            {Object.entries(item.variant.attributes)
                              .map(([k, v]) => `${k}: ${v}`)
                              .join(', ')}
                          </p>
                        )}
                      </div>

                      {item.price_changed && (
                        <div className="mt-1 flex items-center gap-1 text-xs text-amber-600">
                          <AlertCircle className="h-3 w-3" />
                          {t('price_changed')}
                        </div>
                      )}

                      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center rounded-xl border border-gray-200 bg-gray-50">
                          <button
                            type="button"
                            onClick={() =>
                              updateItem.mutate({ itemId: item.id, quantity: item.quantity - 1 })
                            }
                            disabled={item.quantity <= 1}
                            className="flex h-8 w-8 items-center justify-center rounded-l-xl text-gray-500 hover:bg-gray-100 disabled:opacity-40"
                            aria-label="Decrease quantity"
                          >
                            <Minus className="h-3 w-3" />
                          </button>
                          <span className="w-8 text-center text-xs font-semibold text-gray-700">
                            {item.quantity}
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              updateItem.mutate({ itemId: item.id, quantity: item.quantity + 1 })
                            }
                            className="flex h-8 w-8 items-center justify-center rounded-r-xl text-gray-500 hover:bg-gray-100"
                            aria-label="Increase quantity"
                          >
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>

                        <span className="text-sm font-bold font-heading text-gray-800">
                          {formatCurrency(item.subtotal, currency)}
                        </span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Footer */}
          {cart && itemCount > 0 && (
            <div
              className="space-y-4 border-t border-gray-100 bg-gray-50 px-4 py-4 sm:px-6 sm:py-5"
              style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
            >
              <CouponInput />

              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span className="font-semibold text-gray-800">
                    {formatCurrency(cart.subtotal, currency)}
                  </span>
                </div>
                {cart.coupon_discount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Coupon ({cart.coupon_code})</span>
                    <span className="font-semibold">
                      -{formatCurrency(cart.coupon_discount, currency)}
                    </span>
                  </div>
                )}
                {cart.loyalty_discount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Loyalty discount</span>
                    <span className="font-semibold">
                      -{formatCurrency(cart.loyalty_discount, currency)}
                    </span>
                  </div>
                )}
                {cart.applied_promotions.map((promo) => (
                  <div key={promo.id} className="flex justify-between text-green-600">
                    <span>{promo.name}</span>
                    <span className="font-semibold">
                      -{formatCurrency(promo.discount_amount, currency)}
                    </span>
                  </div>
                ))}
                <div className="flex justify-between border-t border-gray-200 pt-3 text-base font-bold text-gray-900 font-heading">
                  <span>Total</span>
                  <span className="text-primary-700">{formatCurrency(cart.grand_total, currency)}</span>
                </div>
              </div>

              <div className="space-y-2">
                <Button className="h-12 w-full rounded-xl font-semibold" asChild onClick={closeCart}>
                  <Link
                    to={canCheckout ? '/checkout' : '/auth/login'}
                    state={canCheckout ? undefined : { from: '/checkout' }}
                  >
                    {canCheckout ? 'Proceed to Checkout' : 'Sign in to Checkout'}
                  </Link>
                </Button>
                <Button variant="outline" className="h-12 w-full rounded-xl font-semibold" asChild onClick={closeCart}>
                  <Link to="/cart">View Cart</Link>
                </Button>
                <Button
                  variant="outline"
                  className="h-10 w-full rounded-xl font-semibold border-red-200 text-red-600 hover:border-red-300 hover:bg-red-50 hover:text-red-700"
                  onClick={handleClearCart}
                >
                  <Trash className="mr-2 h-4 w-4" />
                  Clear Cart
                </Button>
              </div>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
